import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaseCategoriaTorneio } from '../../../../services/categoriaTorneioService';
import { PartidaLite } from '../../../../services/faseGrupoPartidasService';
import { criarFase, atualizarFase } from '../../../../services/faseCategoriaTorneioService';
import { deletarPartida } from '../../../../services/partidaService';
import CreateEditMatchModal from './CreateEditMatchModal';
import MatchSetsResult from '../../../../components/match/MatchSetsResult';
import { atletasDoLado } from '../../../../utils/atletasDoLado';

interface EliminationPhaseTabProps {
  categoriaId: string;
  fasesChave: FaseCategoriaTorneio[];
  onUpdate: () => void;
  canManage?: boolean;
  isAdmin?: boolean;
}

const EliminationPhaseTab: React.FC<EliminationPhaseTabProps> = ({
  categoriaId,
  fasesChave,
  onUpdate,
  canManage = true,
  isAdmin = false,
}) => {
  const [creatingPhase, setCreatingPhase] = useState(false);
  const [showCreatePhaseModal, setShowCreatePhaseModal] = useState(false);
  const [showEditPhaseModal, setShowEditPhaseModal] = useState(false);
  const [editingPhase, setEditingPhase] = useState<FaseCategoriaTorneio | null>(null);
  const [phaseName, setPhaseName] = useState('');
  const [phaseOrdem, setPhaseOrdem] = useState(1);
  const [phaseStatus, setPhaseStatus] = useState('AGENDADA');
  const phaseNameInputRef = useRef<HTMLInputElement>(null);
  const [editingPartida, setEditingPartida] = useState<{
    partida: PartidaLite;
    faseId: string;
  } | null>(null);
  const [creatingPartidaFaseId, setCreatingPartidaFaseId] = useState<string | null>(null);

  // Ordenar fases por ordem
  const fasesOrdenadas = useMemo(() => {
    return [...fasesChave].sort((a, b) => a.ordem - b.ordem);
  }, [fasesChave]);

  const handleOpenCreatePhaseModal = () => {
    setPhaseName('');
    setShowCreatePhaseModal(true);
  };

  const handleCloseCreatePhaseModal = () => {
    setShowCreatePhaseModal(false);
    setPhaseName('');
  };

  const handleCreatePhase = async () => {
    if (!phaseName || !phaseName.trim()) {
      return;
    }

    setCreatingPhase(true);
    try {
      // Calcular ordem baseado apenas nas fases CHAVE existentes
      const ordem = fasesChave.length > 0
        ? Math.max(...fasesChave.map(f => f.ordem)) + 1
        : 1;
      await criarFase({
        categoriaTorneioId: categoriaId,
        nome: phaseName.trim(),
        dataInicio: new Date(),
        dataFim: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 dias
        status: 'AGENDADA',
        ordem,
        tipoGrupoChave: 'CHAVE',
      });
      handleCloseCreatePhaseModal();
      onUpdate();
    } catch (error) {
      console.error('Erro ao criar fase:', error);
      alert('Erro ao criar fase eliminatória');
    } finally {
      setCreatingPhase(false);
    }
  };

  const handleOpenEditPhaseModal = (fase: FaseCategoriaTorneio) => {
    setEditingPhase(fase);
    setPhaseName(fase.nome);
    setPhaseOrdem(fase.ordem);
    setPhaseStatus(fase.status);
    setShowEditPhaseModal(true);
  };

  const handleCloseEditPhaseModal = () => {
    setShowEditPhaseModal(false);
    setEditingPhase(null);
    setPhaseName('');
    setPhaseOrdem(1);
    setPhaseStatus('AGENDADA');
  };

  const handleUpdatePhase = async () => {
    if (!editingPhase || !phaseName || !phaseName.trim()) {
      return;
    }

    setCreatingPhase(true);
    try {
      await atualizarFase(editingPhase.id, {
        categoriaTorneioId: categoriaId,
        nome: phaseName.trim(),
        ordem: phaseOrdem,
        status: phaseStatus,
      });
      handleCloseEditPhaseModal();
      onUpdate();
    } catch (error) {
      console.error('Erro ao atualizar fase:', error);
      alert('Erro ao atualizar fase eliminatória');
    } finally {
      setCreatingPhase(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showCreatePhaseModal) {
          handleCloseCreatePhaseModal();
        }
        if (showEditPhaseModal) {
          handleCloseEditPhaseModal();
        }
      }
    };

    if (showCreatePhaseModal || showEditPhaseModal) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.classList.add('modal-open');
      if (phaseNameInputRef.current) {
        setTimeout(() => phaseNameInputRef.current?.focus(), 0);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (!showCreatePhaseModal && !showEditPhaseModal) {
        document.body.classList.remove('modal-open');
      }
    };
  }, [showCreatePhaseModal, showEditPhaseModal]);

  const handleCreatePartida = (faseId: string) => {
    setCreatingPartidaFaseId(faseId);
  };

  const handleEditPartida = (partida: PartidaLite, faseId: string) => {
    setEditingPartida({ partida, faseId });
  };

  const handleDeletePartida = async (partidaId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta partida?')) {
      return;
    }

    try {
      await deletarPartida(partidaId);
      onUpdate();
    } catch (error) {
      console.error('Erro ao excluir partida:', error);
      alert('Erro ao excluir partida');
    }
  };

  const handleCloseModal = () => {
    setEditingPartida(null);
    setCreatingPartidaFaseId(null);
  };

  const handleModalSuccess = () => {
    handleCloseModal();
    onUpdate();
  };

  // Função para ordenar partidas
  const ordenarPartidas = (partidas: PartidaLite[]): PartidaLite[] => {
    return [...partidas].sort((a, b) => {
      if (a.numeroPartida != null && b.numeroPartida != null) {
        return a.numeroPartida - b.numeroPartida;
      }
      if (a.numeroPartida != null) return -1;
      if (b.numeroPartida != null) return 1;
      // Ordenar por data, tratando datas inválidas
      const dateA = a.data ? new Date(a.data).getTime() : 0;
      const dateB = b.data ? new Date(b.data).getTime() : 0;
      if (isNaN(dateA)) return 1; // Partidas sem data válida vão para o final
      if (isNaN(dateB)) return -1;
      return dateA - dateB;
    });
  };

  return (
    <div className="mt-4">
      {/* Botão criar fase */}
      {canManage && (
        <div className="mb-3">
          <button
            className="btn btn-primary"
            onClick={handleOpenCreatePhaseModal}
            disabled={creatingPhase}
          >
            {creatingPhase ? 'Criando...' : '+ Criar Fase Eliminatória'}
          </button>
        </div>
      )}

      {fasesOrdenadas.length === 0 ? (
        <div className="alert alert-info">
          <p className="mb-0">Nenhuma fase de eliminatórias encontrada.</p>
          <p className="mb-0 mt-2">
            <small>Clique no botão acima para criar uma nova fase eliminatória.</small>
          </p>
        </div>
      ) : (
        <div
          className="d-flex flex-row overflow-x-auto gap-3"
          style={{ minHeight: '400px', paddingBottom: '1rem' }}
        >
          {fasesOrdenadas.map((fase) => {
            const partidasOrdenadas = ordenarPartidas(fase.partidas || []);
            return (
              <div
                key={fase.id}
                className="d-flex flex-column"
                style={{ minWidth: '300px', flexShrink: 0 }}
              >
                {/* Cabeçalho da coluna */}
                <div className="card mb-2">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <div
                      className="flex-grow-1"
                      onClick={canManage ? () => handleOpenEditPhaseModal(fase) : undefined}
                      style={{ cursor: canManage ? 'pointer' : 'default' }}
                      title={canManage ? "Clique para editar a fase" : undefined}
                    >
                      <div className="d-flex align-items-center">
                        <h6 className="mb-0">{fase.nome}</h6>
                        {canManage && <i className="bi bi-pencil ms-2 text-muted" style={{ fontSize: '14px' }}></i>}
                      </div>
                      <small className="text-muted">Ordem: {fase.ordem}</small>
                    </div>
                  </div>
                </div>

                {/* Lista de partidas */}
                <div className="d-flex flex-column gap-2" style={{ minHeight: '300px' }}>
                  {partidasOrdenadas.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <small>Nenhuma partida nesta fase</small>
                    </div>
                  ) : (
                    partidasOrdenadas.map((partida) => (
                      <MatchCard
                        key={partida.id}
                        partida={partida}
                        onEdit={() => handleEditPartida(partida, fase.id)}
                        canManage={canManage}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modais */}
      {editingPartida && (() => {
        const editingFase = fasesOrdenadas.find(f => f.id === editingPartida.faseId);
        const canEditPartida = isAdmin || (!editingFase?.status || editingFase.status === 'AGENDADA' || editingFase.status === 'EM_ANDAMENTO');
        return (
          <CreateEditMatchModal
            partida={editingPartida.partida}
            categoriaId={categoriaId}
            faseCategoriaTorneioId={editingPartida.faseId}
            onClose={handleCloseModal}
            onSuccess={handleModalSuccess}
            canEdit={canEditPartida}
          />
        );
      })()}

      {creatingPartidaFaseId && (() => {
        const creatingFase = fasesOrdenadas.find(f => f.id === creatingPartidaFaseId);
        const canEditCreating = isAdmin || (!creatingFase?.status || creatingFase.status === 'AGENDADA' || creatingFase.status === 'EM_ANDAMENTO');
        return (
          <CreateEditMatchModal
            partida={null}
            categoriaId={categoriaId}
            faseCategoriaTorneioId={creatingPartidaFaseId}
            onClose={handleCloseModal}
            onSuccess={handleModalSuccess}
            canEdit={canEditCreating}
          />
        );
      })()}

      {/* Modal criar fase */}
      {showCreatePhaseModal && (
        <>
          <div className="modal-backdrop fade show" onClick={handleCloseCreatePhaseModal} />
          <div
            className="modal fade show d-block"
            role="dialog"
            tabIndex={-1}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) handleCloseCreatePhaseModal();
            }}
            style={{ overflow: 'auto' }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Criar Fase Eliminatória</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={handleCloseCreatePhaseModal}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Nome da fase</label>
                    <input
                      ref={phaseNameInputRef}
                      type="text"
                      className="form-control"
                      value={phaseName}
                      onChange={(e) => setPhaseName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCreatePhase();
                        }
                        if (e.key === 'Escape') {
                          handleCloseCreatePhaseModal();
                        }
                      }}
                      placeholder="Digite o nome da fase eliminatória"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseCreatePhaseModal}
                    disabled={creatingPhase}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreatePhase}
                    disabled={creatingPhase || !phaseName.trim()}
                  >
                    {creatingPhase ? 'Criando...' : 'Criar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal editar fase */}
      {showEditPhaseModal && editingPhase && (
        <>
          <div className="modal-backdrop fade show" onClick={handleCloseEditPhaseModal} />
          <div
            className="modal fade show d-block"
            role="dialog"
            tabIndex={-1}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) handleCloseEditPhaseModal();
            }}
            style={{ overflow: 'auto' }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Editar Fase Eliminatória</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={handleCloseEditPhaseModal}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Nome da fase</label>
                    <input
                      ref={phaseNameInputRef}
                      type="text"
                      className="form-control"
                      value={phaseName}
                      onChange={(e) => setPhaseName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleUpdatePhase();
                        }
                        if (e.key === 'Escape') {
                          handleCloseEditPhaseModal();
                        }
                      }}
                      placeholder="Digite o nome da fase eliminatória"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Ordem</label>
                    <input
                      type="number"
                      className="form-control"
                      value={phaseOrdem}
                      onChange={(e) => setPhaseOrdem(parseInt(e.target.value) || 1)}
                      min="1"
                      placeholder="Ordem da fase"
                    />
                    <small className="text-muted">A ordem determina a sequência de exibição das fases</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={phaseStatus}
                      onChange={(e) => setPhaseStatus(e.target.value)}
                    >
                      <option value="AGENDADA">Agendada</option>
                      <option value="EM_ANDAMENTO">Em Andamento</option>
                      <option value="FINALIZADA">Finalizada</option>
                      <option value="CANCELADA">Cancelada</option>
                    </select>
                    <small className="text-muted">
                      Alterar o status para "Agendada" ou "Em Andamento" permite editar as partidas desta fase
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseEditPhaseModal}
                    disabled={creatingPhase}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleUpdatePhase}
                    disabled={creatingPhase || !phaseName.trim()}
                  >
                    {creatingPhase ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Componente de Card de Partida
interface MatchCardProps {
  partida: PartidaLite;
  onEdit: () => void;
  canManage?: boolean;
}

const MatchCard: React.FC<MatchCardProps> = ({ partida, onEdit, canManage = true }) => {
  const fallbackAvatar = '/assets/images/user.png';

  return (
    <div className="card shadow-sm">
      <div className="card-body p-3">
        {/* Número da partida */}
        {partida.numeroPartida != null && (
          <div className="mb-2">
            <small className="text-muted">Partida #{partida.numeroPartida}</small>
          </div>
        )}

        {/* Layout em 3 colunas: Equipe A | Placar | Equipe B */}
        <div className="d-flex align-items-center mb-3">
          {/* Equipe A */}
          <div className="flex-fill">
            <div className="d-flex flex-column gap-2">
              {(() => {
                const atletas = atletasDoLado(partida, 'A');
                return atletas.length > 0 ? (
                  atletas.map((atleta) => (
                    <div key={atleta.id} className="d-flex align-items-center gap-2">
                      <img
                        src={atleta.imagemUrl || fallbackAvatar}
                        alt={atleta.nome}
                        className="rounded-circle"
                        style={{
                          width: '32px',
                          height: '32px',
                          objectFit: 'cover',
                        }}
                      />
                      <div style={{ fontSize: '12px' }}>
                        {atleta.nome}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-muted" style={{ fontSize: '12px' }}>
                    Equipe A não definida
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Placar */}
          <div className="text-center px-3">
            <MatchSetsResult
              placar={partida.placar}
              equipeAWO={partida.equipeAWO}
              equipeBWO={partida.equipeBWO}
              equipeADesistencia={partida.equipeADesistencia}
              equipeBDesistencia={partida.equipeBDesistencia}
              useMaterialUI={false}
            />
          </div>

          {/* Equipe B */}
          <div className="flex-fill">
            <div className="d-flex flex-column gap-2">
              {(() => {
                const atletas = atletasDoLado(partida, 'B');
                return atletas.length > 0 ? (
                  atletas.map((atleta) => (
                    <div key={atleta.id} className="d-flex align-items-center gap-2">
                      <img
                        src={atleta.imagemUrl || fallbackAvatar}
                        alt={atleta.nome}
                        className="rounded-circle"
                        style={{
                          width: '32px',
                          height: '32px',
                          objectFit: 'cover',
                        }}
                      />
                      <div style={{ fontSize: '12px' }}>
                        {atleta.nome}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-muted" style={{ fontSize: '12px' }}>
                    Equipe B não definida
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Botões de ação */}
        {canManage && (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary flex-fill"
              onClick={onEdit}
            >
              Editar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EliminationPhaseTab;

